import React, { useState, useEffect } from 'react';
import { X, Download, Smartphone } from 'lucide-react';

export const PWAInstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    setIsStandalone(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Check if dismissed recently (within 7 days)
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissed) {
      const dismissedDate = new Date(dismissed);
      const daysSinceDismissed = (new Date() - dismissedDate) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) return;
    }

    // Listen for beforeinstallprompt event (Android/Desktop)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt after 30 seconds of browsing
      setTimeout(() => setShowPrompt(true), 30000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Show iOS prompt after delay if on iOS and not standalone
    if (iOS && !standalone) {
      setTimeout(() => setShowPrompt(true), 30000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log('PWA install outcome:', outcome);
      setDeferredPrompt(null);
    }
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    localStorage.setItem('pwa-prompt-dismissed', new Date().toISOString());
    setShowPrompt(false);
  };

  if (!showPrompt || isStandalone) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 z-50 animate-in slide-in-from-bottom duration-300 md:left-auto md:right-4 md:w-96">
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4 shadow-2xl">
        <button 
          onClick={handleDismiss}
          className="absolute top-3 right-3 text-zinc-500 hover:text-white transition-colors"
          aria-label="Dismiss"
        >
          <X size={20} />
        </button>

        <div className="flex items-start gap-4">
          <div className="bg-yellow-500/20 p-3 rounded-xl flex-shrink-0">
            <Smartphone className="w-8 h-8 text-yellow-500" />
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-white text-lg mb-1">
              Install AutoTrust GH
            </h3>
            <p className="text-zinc-400 text-sm mb-3">
              Add to your home screen for quick access and offline browsing
            </p>

            {isIOS ? (
              <div className="text-sm text-zinc-300 bg-zinc-800 rounded-lg p-3">
                <p className="mb-2">To install:</p>
                <ol className="list-decimal list-inside space-y-1 text-zinc-400">
                  <li>Tap the <span className="text-white">Share</span> button</li>
                  <li>Scroll and tap <span className="text-white">"Add to Home Screen"</span></li>
                  <li>Tap <span className="text-white">"Add"</span></li>
                </ol>
              </div>
            ) : (
              <button
                onClick={handleInstall}
                className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold px-4 py-2 rounded-full transition-colors text-sm"
              >
                <Download size={16} />
                Install App
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
